<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>Trigger</form-label>
            <div v-if="trigger" class="flex items-center justify-between gap-2 text-xs">
                <button class="truncate text-left hover:text-ui-active" title="Select the trigger"
                    @click="hass.select(page, 'node', trigger.id)">⚡ on {{ trigger.data.event || '(no event)' }}</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the trigger"
                    @click="hass.removeWire(page, triggerWire.id)">×</button>
            </div>
            <p v-else class="text-xs text-gray-300">Drag a wire from a trigger's output to this condition.</p>
        </section>

        <form-select v-if="buttons.length" name="Button" :model-value="draft.filter ? String(draft.filter.val) : ''"
            @update:model-value="value => draft.filter = value === '' ? null : { val: Number(value) }">
            <option value="">Any button</option>
            <option v-for="button in buttons" :key="button.value" :value="String(button.value)">{{ button.label }}</option>
        </form-select>

        <section class="space-y-1.5">
            <form-label>Conditions (all must hold)</form-label>
            <ConditionEditor v-for="(condition, index) in draft.conditions" :key="condition.id"
                :condition="condition" :runtimeVariables="variables" :node="object"
                :entityId="entityId" :settings="settings"
                @remove="draft.conditions.splice(index, 1)" />
            <button class="ha-builder-link-button" @click="addCondition">+ condition</button>
        </section>

        <HassRunList :page="page" :graph="graph" :node="node" label="Then (in order)"
            empty="Drag a wire from this condition's output to an action." />
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { findNode } from '../../graph/graphModel.js';
import { getConditionEntityId, getConditionTrigger, getTriggerSource, getWiresInto } from '../../hass/hassGraph.js';
import { getAutomationMetaByType } from '../../automation/schema/WidgetAutomationMeta.js';
import { getBtnmatrixButtons } from '../../automation/btnmatrixButtons.js';
import { AutomationCondition } from '../../automation/ast/AutomationCondition.js';
import { AutomationValue } from '../../automation/ast/AutomationValue.js';
import ConditionEditor from '../hassbuilder/automation/ConditionEditor.vue';
import HassRunList from './HassRunList.vue';
import { useNodeDraft } from './useNodeDraft.js';

/**
 * A condition node: one branch of its trigger's `choose`. Its conditions all
 * hold for the branch to run; on a btnmatrix it can also filter the button.
 */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
    settings: { type: Object, default: () => ({}) },
});
const hass = useHassFlowStore();

const triggerWire = computed(() => getWiresInto(props.graph, { node: props.node.id, port: 'in' })[0] ?? null);
const trigger = computed(() => {
    const id = getConditionTrigger(props.graph, props.node.id);
    return id ? findNode(props.graph, id) : null;
});
const object = computed(() => {
    const uuid = trigger.value ? getTriggerSource(props.graph, trigger.value.id) : null;
    return uuid ? props.context.getObject(uuid) : null;
});
const variables = computed(() => (object.value ? getAutomationMetaByType(object.value.obj)?.runtimeVariables ?? [] : []));
const entityId = computed(() => getConditionEntityId(props.graph, props.node.id));
const buttons = computed(() => (object.value?.obj === 'btnmatrix' ? getBtnmatrixButtons(object.value) : []));

// ConditionEditor edits AutomationCondition instances in place (see useNodeDraft).
const draft = useNodeDraft({
    build: () => {
        const data = toRaw(props.node).data;
        return {
            conditions: (data.conditions ?? []).map(condition => AutomationCondition.fromJSON(structuredClone(condition))),
            filter: data.filter ? structuredClone(data.filter) : null,
        };
    },
    snapshot: value => ({ conditions: value.conditions.map(condition => condition.toJSON()), filter: value.filter ?? null }),
    current: () => ({ conditions: props.node.data.conditions ?? [], filter: props.node.data.filter ?? null }),
    commit: data => hass.setNodeData(props.page, props.node.id, { ...toRaw(props.node).data, ...data }),
});

function addCondition() {
    const first = variables.value[0];
    draft.value.conditions.push(new AutomationCondition({
        left: AutomationValue.reference(first?.name ?? ''),
        operator: '==',
        right: AutomationValue.literal(''),
    }));
}
</script>
